<div class="space-y-6">

  <!-- Loading -->
  <div *ngIf="loading" class="flex items-center gap-2 py-10 text-sm text-slate-400">
    <lucide-icon name="loader" [size]="16" class="animate-spin" [strokeWidth]="2"></lucide-icon>
    Loading subscription details...
  </div>

  <!-- Load error -->
  <div *ngIf="loadError" class="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
    <lucide-icon name="triangle-alert" [size]="15" class="mt-0.5 flex-shrink-0 text-red-500" [strokeWidth]="2"></lucide-icon>
    <p class="text-sm text-red-700">{{ loadError }}</p>
  </div>

  <ng-container *ngIf="!loading && !loadError">

    <!-- Post-checkout processing. The redirect is not proof of payment: we wait
         for the verified webhook to grant entitlement server-side. -->
    <div *ngIf="processing" class="flex items-start gap-3 rounded-lg border border-violet-200 bg-violet-50 px-4 py-3">
      <lucide-icon name="loader" [size]="15" class="mt-0.5 flex-shrink-0 animate-spin text-violet-500" [strokeWidth]="2"></lucide-icon>
      <div>
        <p class="text-sm font-medium text-violet-800">Processing your subscription</p>
        <p class="mt-0.5 text-xs text-violet-700">
          Payment received. We're confirming it with Stripe — your plan will update here automatically.
        </p>
      </div>
    </div>

    <!-- Payment problem / grace period -->
    <div *ngIf="graceMessage" class="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
      <lucide-icon name="triangle-alert" [size]="15" class="mt-0.5 flex-shrink-0 text-amber-500" [strokeWidth]="2"></lucide-icon>
      <div class="min-w-0 flex-1">
        <p class="text-sm text-amber-800">{{ graceMessage }}</p>
        <button
          *ngIf="canManageBilling"
          (click)="openPortal()"
          [disabled]="!!redirecting"
          class="mt-2 text-xs font-semibold text-amber-900 underline underline-offset-2 hover:text-amber-950 disabled:cursor-not-allowed disabled:opacity-60"
        >Update payment method</button>
      </div>
    </div>

    <!-- Action feedback -->
    <div *ngIf="actionError" class="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
      <lucide-icon name="triangle-alert" [size]="15" class="mt-0.5 flex-shrink-0 text-red-500" [strokeWidth]="2"></lucide-icon>
      <p class="text-sm text-red-700">{{ actionError }}</p>
    </div>

    <!-- Current plan card -->
    <div class="overflow-hidden rounded-xl border border-violet-300 bg-white shadow-sm ring-2 ring-violet-100">
      <div class="flex items-center justify-between border-b border-slate-100 px-6 py-4">
        <p class="text-xs font-semibold uppercase tracking-wide text-slate-400">Current Plan</p>
        <button
          *ngIf="canManageBilling"
          (click)="openPortal()"
          [disabled]="!!redirecting"
          class="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <lucide-icon
            *ngIf="redirecting === 'portal'"
            name="loader" [size]="13" class="animate-spin" [strokeWidth]="2.5"
          ></lucide-icon>
          <lucide-icon
            *ngIf="redirecting !== 'portal'"
            name="external-link" [size]="13" [strokeWidth]="2"
          ></lucide-icon>
          {{ redirecting === 'portal' ? 'Opening...' : 'Manage billing' }}
        </button>
      </div>
      <div class="flex items-center gap-5 px-6 py-5">
        <div class="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-violet-100">
          <lucide-icon name="credit-card" [size]="22" class="text-violet-600" [strokeWidth]="1.75"></lucide-icon>
        </div>
        <div class="min-w-0 flex-1">
          <div class="flex flex-wrap items-center gap-2">
            <p class="text-lg font-bold text-slate-900">{{ currentPlan.name }}</p>
            <span
              class="rounded-full px-2.5 py-0.5 text-xs font-semibold"
              [class.bg-emerald-100]="statusIsHealthy"
              [class.text-emerald-700]="statusIsHealthy"
              [class.bg-amber-100]="!statusIsHealthy"
              [class.text-amber-700]="!statusIsHealthy"
            >{{ statusLabel }}</span>
            <span
              *ngIf="summary?.entitlementSource === 'MANUAL'"
              class="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600"
            >Managed plan</span>
          </div>
          <p class="mt-0.5 text-sm text-slate-500">{{ currentPlan.description }}</p>
          <p *ngIf="summary?.currentPeriodEnd" class="mt-1 text-xs text-slate-400">
            {{ summary?.cancelAtPeriodEnd ? 'Access ends' : 'Renews' }}
            {{ summary?.currentPeriodEnd | date:'mediumDate' }}
          </p>
        </div>
        <div class="flex-shrink-0 text-right">
          <p class="text-2xl font-bold text-slate-900">
            {{ currentPlan.price }}<span class="text-sm font-normal text-slate-400">{{ currentPlan.period }}</span>
          </p>
        </div>
      </div>
    </div>

    <!-- Managed-plan notice: Stripe self-serve is intentionally disabled here. -->
    <div
      *ngIf="summary?.entitlementSource === 'MANUAL'"
      class="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3"
    >
      <lucide-icon name="info" [size]="15" class="mt-0.5 flex-shrink-0 text-slate-400" [strokeWidth]="2"></lucide-icon>
      <p class="text-sm text-slate-600">
        This organization is on a managed plan. Contact your account team to make changes.
      </p>
    </div>

    <!-- Plans -->
    <div class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div class="border-b border-slate-200 px-6 py-4">
        <h2 class="text-sm font-semibold text-slate-900">Plans</h2>
        <p class="mt-0.5 text-xs text-slate-500">
          Upgrades are processed securely by Stripe. We never see or store your card details.
        </p>
      </div>
      <div class="grid grid-cols-1 gap-4 p-6 sm:grid-cols-3">
        <div
          *ngFor="let plan of visiblePlans"
          class="relative flex flex-col rounded-xl border p-5 transition-shadow"
          [class.border-violet-400]="plan.tier === currentTier"
          [class.ring-2]="plan.tier === currentTier"
          [class.ring-violet-200]="plan.tier === currentTier"
          [class.shadow-sm]="plan.tier === currentTier"
          [class.border-slate-200]="plan.tier !== currentTier"
        >
          <div class="mb-1 flex items-center justify-between">
            <p class="text-sm font-semibold text-slate-900">{{ plan.name }}</p>
            <span
              *ngIf="plan.tier === currentTier"
              class="rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-semibold text-violet-700"
            >Current</span>
          </div>
          <p class="text-xl font-bold text-slate-900">
            {{ plan.price }}<span class="text-xs font-normal text-slate-400">{{ plan.period }}</span>
          </p>
          <p class="mt-2 text-xs text-slate-500">{{ plan.description }}</p>
          <ul class="mt-4 flex-1 space-y-1.5">
            <li *ngFor="let f of plan.features" class="flex items-center gap-1.5 text-xs text-slate-600">
              <lucide-icon name="circle-check" [size]="13" class="flex-shrink-0 text-violet-500" [strokeWidth]="2"></lucide-icon>
              {{ f }}
            </li>
          </ul>

          <!-- Self-serve upgrade — only for eligible orgs and authorized users -->
          <button
            *ngIf="showUpgradeButton(plan)"
            (click)="startUpgrade(plan.tier)"
            [disabled]="!!redirecting || processing"
            class="mt-5 flex w-full items-center justify-center gap-1.5 rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-violet-700 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
          >
            <lucide-icon
              *ngIf="redirecting === 'checkout'"
              name="loader" [size]="14" class="animate-spin" [strokeWidth]="2.5"
            ></lucide-icon>
            {{ redirecting === 'checkout' ? 'Redirecting to Stripe...' : 'Upgrade to ' + plan.name }}
          </button>

          <!-- Current plan -->
          <button
            *ngIf="plan.tier === currentTier"
            disabled
            class="mt-5 w-full cursor-not-allowed rounded-lg bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-400"
          >Current plan</button>

          <!-- Admin-assigned plan (CUSTOM): never purchasable here. -->
          <p
            *ngIf="!plan.selfServe && plan.tier !== currentTier && plan.tier === 'CUSTOM'"
            class="mt-5 rounded-lg border border-dashed border-slate-200 px-4 py-2 text-center text-xs text-slate-500"
          >Arranged with your account team</p>

          <!-- Downgrades are handled in the Stripe portal, not here. -->
          <button
            *ngIf="plan.tier === 'FREE' && plan.tier !== currentTier && canManageBilling"
            (click)="openPortal()"
            [disabled]="!!redirecting"
            class="mt-5 w-full rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >Cancel in portal</button>
        </div>
      </div>
    </div>

    <!-- Invoices live in the Stripe-hosted portal; we deliberately do not
         rebuild invoice/payment-method UI. -->
    <div class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div class="flex items-center justify-between px-6 py-5">
        <div class="min-w-0">
          <h2 class="text-sm font-semibold text-slate-900">Invoices &amp; payment methods</h2>
          <p class="mt-0.5 text-xs text-slate-500">
            {{ canManageBilling
              ? 'Invoice history, receipts, payment methods, and cancellation are managed in the Stripe portal.'
              : 'Available once your organization has a paid subscription.' }}
          </p>
        </div>
        <button
          *ngIf="canManageBilling"
          (click)="openPortal()"
          [disabled]="!!redirecting"
          class="flex flex-shrink-0 items-center gap-1.5 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <lucide-icon
            *ngIf="redirecting === 'portal'"
            name="loader" [size]="14" class="animate-spin" [strokeWidth]="2.5"
          ></lucide-icon>
          <lucide-icon
            *ngIf="redirecting !== 'portal'"
            name="external-link" [size]="14" [strokeWidth]="2"
          ></lucide-icon>
          {{ redirecting === 'portal' ? 'Opening...' : 'Open billing portal' }}
        </button>
      </div>
    </div>

  </ng-container>

</div>
